import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { formatDate } from "@/lib/formatDate";
import { formatDisplayNumber } from "../../utils/formatNumber";
import { Invoice } from "../../types";
import { NAVIGATION, INVOICE_STATUSES, ROLES } from "../../constants";
import { useInvoices } from "../../hooks/useInvoices";
import { useNavigationState } from "../../hooks/useNavigationState";
import DataTable from "../../components/tables/DataTable";
import FilesColumn from "../../components/files/FilesColumn";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { useAuth } from "../../contexts/AuthContext";

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state: navState, updateState } = useNavigationState();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const isAdmin = user?.role === ROLES.ADMIN;

  const { invoices, isLoading, error, deleteInvoice, isDeleting } =
    useInvoices();

  const columns: GridColDef<Invoice>[] = [
    {
      field: "id",
      headerName: "#",
      width: 15,
      filterable: false,
      sortable: true,
      valueFormatter: (value: number, row: Invoice) =>
        value ? invoices.indexOf(row) + 1 : 0,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      flex: 1,
      filterable: true,
      sortable: true,
    },
    {
      field: "client",
      headerName: "Client",
      flex: 1,
      filterable: true,
      sortable: true,
      valueGetter: (value: any) => value?.name || "",
    },
    {
      field: "project",
      headerName: "Project",
      flex: 1,
      filterable: true,
      sortable: true,
      valueGetter: (value: any) => value?.name || "",
    },
    {
      field: "totalAmount",
      headerName: "Total Amount",
      flex: 1,
      filterable: true,
      sortable: true,
      valueFormatter: (value: any) => formatDisplayNumber(value),
    },
    {
      field: "currency",
      headerName: "Currency",
      flex: 0.5,
      filterable: true,
      sortable: true,
    },
    {
      field: "issueDate",
      headerName: "Issue Date",
      flex: 1,
      filterable: true,
      sortable: true,
      valueFormatter: (value: any) => (value ? formatDate(value) : ""),
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 1,
      filterable: true,
      sortable: true,
      valueFormatter: (value: any) => (value ? formatDate(value) : ""),
    },
    {
      field: "paidDate",
      headerName: "Paid Date",
      flex: 1,
      filterable: true,
      sortable: true,
      valueFormatter: (value: any) => (value ? formatDate(value) : ""),
    },
    {
      field: "status",
      headerName: "Status",
      width: 80,
      filterable: true,
      sortable: true,
      renderCell: (params: any) => {
        const status = params?.value;
        if (!status) return null;

        const getStatusColor = (status: string) => {
          switch (status?.toUpperCase()) {
            case INVOICE_STATUSES.PAID:
              return "success";
            case INVOICE_STATUSES.SENT:
              return "info";
            case INVOICE_STATUSES.DRAFT:
              return "default";
            case INVOICE_STATUSES.OVERDUE:
              return "error";
            case INVOICE_STATUSES.UNPAID:
              return "warning";
            default:
              return "default";
          }
        };

        return (
          <Chip
            label={status}
            color={getStatusColor(status) as any}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: "files",
      headerName: "Files",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <FilesColumn entityType="INVOICE" entityId={params.row.id} />
      ),
    },
  ];

  // Add actions column for admins
  if (isAdmin) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Delete Invoice">
          <IconButton
            size="small"
            color="error"
            onClick={(event: any) => {
              event.stopPropagation();
              setSelectedInvoice(params.row);
              setDeleteDialogOpen(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    });
  }

  const handleDeleteInvoice = () => {
    if (selectedInvoice) {
      deleteInvoice(selectedInvoice.id);
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedInvoice(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Invoices
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(NAVIGATION.ADD_INVOICE)}
        >
          Add Invoice
        </Button>
      </Box>

      {error instanceof Error && (
        <Typography color="error">{error.message}</Typography>
      )}

      <DataTable
        rows={invoices}
        columns={columns}
        loading={isLoading}
        navigationPath={NAVIGATION.INVOICES}
        initialPage={navState.page}
        onPageChange={(page) => updateState({ page })}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteInvoice}
        title="Delete Invoice"
        itemName={selectedInvoice?.number?.toString() || ""}
        itemType="invoice"
        loading={isDeleting}
      />
    </Box>
  );
};

export default Invoices;
